/**
 * SearchPage Component
 * Global search across customers and team members (users).
 *
 * Results come from the per-entity services (`getCustomerService`,
 * `getUserService`), which transparently return mock data in mock mode and
 * live API data in live mode. There is no static import of mock data here, so
 * the page works identically in both modes.
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  FileSearch,
  User,
  Users,
  Calendar,
  MapPin,
  Mail,
  Phone,
  ArrowRight,
  X,
  Loader2,
} from 'lucide-react'
import { getCustomerService } from '@/services/customers'
import { getUserService } from '@/services/users'
import { useAuth } from '@/contexts/AuthContext'
import { getRoleDisplayName } from '@/types/user.types'
import type { IndividualCustomer } from '@/types/customer'
import type { User as TeamUser } from '@/types/user.types'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

/**
 * Filter customers client-side by name, PAN, email, phone, location, and
 * employer. The backend customer-service has no full-text search endpoint, so
 * the page fetches the list and narrows it locally.
 */
function filterCustomers(customers: IndividualCustomer[], query: string): IndividualCustomer[] {
  const q = query.toLowerCase().trim()
  if (!q) return []
  return customers.filter(
    c =>
      c.fullName.toLowerCase().includes(q) ||
      c.pan.toLowerCase().includes(q) ||
      c.emailPrimary.toLowerCase().includes(q) ||
      c.mobilePrimary.includes(q) ||
      (c.aadhaar !== undefined && c.aadhaar.includes(q)) ||
      (c.employerName !== undefined && c.employerName.toLowerCase().includes(q)) ||
      c.residentialAddress.city.toLowerCase().includes(q) ||
      c.residentialAddress.state.toLowerCase().includes(q)
  )
}

/**
 * Filter team members client-side by name, email, and phone.
 */
function filterUsers(users: TeamUser[], query: string): TeamUser[] {
  const q = query.toLowerCase().trim()
  if (!q) return []
  return users.filter(
    u =>
      u.fullName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.phone !== undefined && u.phone.includes(q))
  )
}

export function SearchPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const tenantId = user?.tenantId

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const [customers, setCustomers] = useState<IndividualCustomer[]>([])
  const [users, setUsers] = useState<TeamUser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch results from the services whenever the debounced query changes.
  useEffect(() => {
    const trimmed = debouncedQuery.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setCustomers([])
      setUsers([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    const run = async () => {
      try {
        // Customers are visible to every authenticated role; team members are
        // only available when we have a tenant context (owner/admin scope).
        const [customerResults, userResults] = await Promise.all([
          getCustomerService().list(),
          tenantId
            ? getUserService()
                .list(tenantId)
                .catch(() => [] as TeamUser[])
            : Promise.resolve([] as TeamUser[]),
        ])

        if (cancelled) return
        setCustomers(customerResults)
        setUsers(userResults)
      } catch {
        if (cancelled) return
        setCustomers([])
        setUsers([])
        setError('Something went wrong while searching. Please try again.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, tenantId])

  // Derive filtered, grouped results during render.
  const customerResults = useMemo(
    () => filterCustomers(customers, debouncedQuery),
    [customers, debouncedQuery]
  )
  const userResults = useMemo(() => filterUsers(users, debouncedQuery), [users, debouncedQuery])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setDebouncedQuery('')
  }

  const totalResults = customerResults.length + userResults.length
  const showResults = debouncedQuery.trim().length >= MIN_QUERY_LENGTH

  const getStatusBadge = (status: IndividualCustomer['status']) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full'
    switch (status) {
      case 'active':
        return (
          <span
            className={`${baseClasses} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400`}
          >
            Active
          </span>
        )
      case 'inactive':
        return (
          <span
            className={`${baseClasses} bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400`}
          >
            Inactive
          </span>
        )
      case 'archived':
        return (
          <span
            className={`${baseClasses} bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200`}
          >
            Archived
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary mb-2">TaxHive Search</h1>
        <p className="text-text-secondary">
          Search across customers and your team by name, PAN, email, or phone
        </p>
      </div>

      {/* Search Bar */}
      <div className="card card-elevated p-6 mb-6">
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
            <Search className="h-5 w-5" />
          </div>

          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by name, PAN, email, phone, employer, city..."
            className="input input-bordered pl-12 pr-12 w-full text-lg"
            autoFocus
          />

          {searchQuery && (
            <button
              onClick={handleClearSearch}
              aria-label="Clear search"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Search Tips */}
        {!showResults && (
          <div className="mt-4 p-4 bg-surface-overlay rounded-lg">
            <p className="text-sm text-text-secondary font-medium mb-2">Search Tips:</p>
            <ul className="text-sm text-text-tertiary space-y-1 list-disc list-inside">
              <li>Search customers by name (e.g., "Rajesh Kumar")</li>
              <li>Search by PAN number (e.g., "ABCDE1234F")</li>
              <li>Search by email or phone number</li>
              <li>Find team members by name or email</li>
            </ul>
          </div>
        )}

        {/* Result Count */}
        {showResults && !isLoading && !error && (
          <div className="mt-4 text-sm text-text-secondary">
            Found{' '}
            <span className="font-semibold text-primary-600 dark:text-primary-400">
              {totalResults}
            </span>{' '}
            {totalResults === 1 ? 'result' : 'results'} for "{debouncedQuery}"
          </div>
        )}
      </div>

      {/* Loading */}
      {showResults && isLoading && (
        <div className="card card-elevated p-12 text-center">
          <Loader2
            className="h-8 w-8 text-primary-500 animate-spin mx-auto mb-4"
            aria-hidden="true"
          />
          <p className="text-text-secondary">Searching…</p>
        </div>
      )}

      {/* Error */}
      {showResults && !isLoading && error && (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <FileSearch className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">Search failed</h3>
          <p className="text-text-secondary">{error}</p>
        </div>
      )}

      {/* No Results */}
      {showResults && !isLoading && !error && totalResults === 0 && (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
            <FileSearch className="h-8 w-8 text-text-tertiary" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">No results found</h3>
          <p className="text-text-secondary mb-4">
            No customers or team members found for "{debouncedQuery}". Try different keywords or
            check your spelling.
          </p>
          <div className="text-sm text-text-tertiary">
            <p>Suggestions:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Use fewer or more general keywords</li>
              <li>Check for typos in your search query</li>
              <li>Try searching by PAN, email, or phone number</li>
            </ul>
          </div>
        </div>
      )}

      {/* Results */}
      {showResults && !isLoading && !error && totalResults > 0 && (
        <div className="space-y-8">
          {/* Customers group */}
          {customerResults.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                Customers
                <span className="text-sm font-normal text-text-secondary">
                  ({customerResults.length})
                </span>
              </h2>
              <div className="grid gap-4">
                {customerResults.map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    className="card card-elevated p-6 hover:shadow-lg transition cursor-pointer group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 bg-surface-overlay rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                          <User className="h-5 w-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Customer
                            </span>
                            {getStatusBadge(customer.status)}
                            {customer.pendingAssessments > 0 && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200">
                                {customer.pendingAssessments} pending
                              </span>
                            )}
                          </div>

                          <h3 className="text-lg font-semibold text-text-primary mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {customer.fullName}
                          </h3>

                          <p className="text-sm text-text-secondary mb-2">
                            PAN:{' '}
                            <code className="font-mono text-sm bg-surface-overlay px-2 py-1 rounded">
                              {customer.pan}
                            </code>
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-text-tertiary opacity-0 group-hover:opacity-100 transition" />
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-text-secondary">
                        <MapPin className="h-4 w-4 text-text-tertiary" />
                        <span>
                          {customer.residentialAddress.city}, {customer.residentialAddress.state}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-text-secondary">
                        <User className="h-4 w-4 text-text-tertiary" />
                        <span className="capitalize">{customer.occupation.replace('_', ' ')}</span>
                      </div>

                      <div className="flex items-center gap-2 text-text-secondary">
                        <Calendar className="h-4 w-4 text-text-tertiary" />
                        <span>{customer.totalAssessments} assessments</span>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="mt-4 pt-3 border-t border-surface-overlay flex flex-wrap gap-4 text-sm text-text-tertiary">
                      <span>{customer.emailPrimary}</span>
                      <span>{customer.mobilePrimary}</span>
                      {customer.assignedCAName && <span>CA: {customer.assignedCAName}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team (users) group */}
          {userResults.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                Team
                <span className="text-sm font-normal text-text-secondary">
                  ({userResults.length})
                </span>
              </h2>
              <div className="grid gap-4">
                {userResults.map(teamUser => (
                  <div
                    key={teamUser.userId}
                    onClick={() => navigate(`/users/${teamUser.userId}`)}
                    className="card card-elevated p-6 hover:shadow-lg transition cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 bg-surface-overlay rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors">
                          <Users className="h-5 w-5 text-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                              Team
                            </span>
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-surface-overlay text-text-secondary">
                              {getRoleDisplayName(teamUser.role)}
                            </span>
                            {teamUser.isActive !== true && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                {teamUser.isActive === false ? 'Inactive' : 'Not recorded'}
                              </span>
                            )}
                          </div>

                          <h3 className="text-lg font-semibold text-text-primary mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {teamUser.fullName}
                          </h3>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-text-tertiary opacity-0 group-hover:opacity-100 transition" />
                    </div>

                    {/* Contact Info */}
                    <div className="mt-4 pt-3 border-t border-surface-overlay flex flex-wrap gap-4 text-sm text-text-tertiary">
                      <span className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-text-tertiary" />
                        {teamUser.email}
                      </span>
                      {teamUser.phone && (
                        <span className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-text-tertiary" />
                          {teamUser.phone}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
